/**
 * Image URL utility functions
 * Handles WordPress image paths and makes them work across different hosts
 */

// WordPress subdirectory - hardcoded for this installation
// Change this if WordPress is installed in a different directory
const WORDPRESS_SUBDIR = '/wordpress';

/**
 * Get the home URL from BOAT_TRADER_API global or return empty string
 */
export function getHomeUrl(): string {
  if (typeof window !== 'undefined' && (window as any).BOAT_TRADER_API?.home_url) {
    return (window as any).BOAT_TRADER_API.home_url;
  }
  return '';
}

/**
 * Get the content URL from BOAT_TRADER_API global or return empty string
 */
export function getContentUrl(): string {
  if (typeof window !== 'undefined' && (window as any).BOAT_TRADER_API?.content_url) {
    return (window as any).BOAT_TRADER_API.content_url;
  }
  return '';
}

/**
 * Try to get WebP version of image URL
 * Returns WebP URL if it exists, otherwise null
 *
 * Regole di mirroring (generate dal plugin, vedi Image_Handler::get_webp_target_path):
 * - /uploads/boat-trader/boats/YYYY/MM/nome.ext  ->  /uploads/boat-trader/webp/YYYY/MM/nome.webp
 * - /uploads/YYYY/MM/nome.ext (Media Library, es. blog) -> /uploads/boat-trader/webp/media/YYYY/MM/nome.webp
 */
export function getWebPUrl(url: string): string | null {
  if (!url) return null;

  let path: string;
  let origin = '';

  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      const parsed = new URL(url);
      path = parsed.pathname;
      origin = parsed.origin;
    } catch {
      return null;
    }
  } else {
    path = url;
    const homeUrl = getHomeUrl();
    if (!homeUrl) return null;
    origin = homeUrl.replace(/\/$/, '');
  }

  if (!path.startsWith('/')) {
    path = '/' + path;
  }

  let webpPath: string | null = null;

  if (/\.(jpg|jpeg|png|gif)$/i.test(path)) {
    if (path.includes('/boat-trader/boats/')) {
      // Convert /boat-trader/boats/ -> /boat-trader/webp/
      const replaced = path
        .replace('/boat-trader/boats/', '/boat-trader/webp/')
        .replace(/\.(jpg|jpeg|png|gif)$/i, '.webp');
      if (replaced !== path) webpPath = replaced;
    } else if (path.includes('/dealer-logos/')) {
      // Dealer logos: /dealer-logos/name.jpg -> /dealer-logos/name.webp
      const replaced = path.replace(/\.(jpg|jpeg|png|gif)$/i, '.webp');
      if (replaced !== path) webpPath = replaced;
    } else if (!path.includes('/boat-trader/')) {
      // Media Library: /uploads/YYYY/MM/nome.ext -> /uploads/boat-trader/webp/media/YYYY/MM/nome.webp
      // Comprende le varianti ridimensionate WP (nome-WxH.ext)
      const replaced = path.replace(
        /^(.+\/uploads\/)(\d{4}\/\d{2}\/.+)\.(jpg|jpeg|png|gif)$/i,
        '$1boat-trader/webp/media/$2.webp'
      );
      if (replaced !== path) webpPath = replaced;
    }
  }

  if (!webpPath) return null;

  let result = `${origin}${webpPath}`;

  // Fix doubled domain
  result = result.replace(/https?:\/\/sealevel\.ithttps?:\/\/sealevel\.it/gi, 'https://sealevel.it');

  return result;
}

/**
 * Get the plugin URL from BOAT_TRADER_API global or return empty string
 * @param filename - Optional filename to append to the plugin URL
 */
export function getPluginUrl(filename: string = ''): string {
  // First, try to use plugin_url from BOAT_TRADER_API (set by PHP)
  // This is the most reliable method since PHP uses WordPress home_url()
  const apiPluginUrl = (window as any).BOAT_TRADER_API?.plugin_url;
  if (apiPluginUrl) {
    // Add cache-busting query parameter to avoid browser/SW caching issues
    const cacheVersion = 'v=20260305163000';
    const separator = filename ? (apiPluginUrl.endsWith('/') ? '' : '/') : '';
    const result = apiPluginUrl + separator + filename + (filename ? '?' + cacheVersion : '');
    return result;
  }
  
  // Fallback: Try to get home_url from BOAT_TRADER_API
  const homeUrl = getHomeUrl();
  if (homeUrl) {
    // home_url includes WordPress subdirectory path (e.g., /wordpress)
    // We need: http://localhost/wordpress/wp-content/plugins/boat-trader-v3/assets/images/
    const result = homeUrl + '/wp-content/plugins/boat-trader-v3/assets/images/' + filename;
    return result;
  }
  
  // ULTIMATE FALLBACK: Use hardcoded WordPress subdirectory
  // This ensures the correct path even if BOAT_TRADER_API is not set
  const result = WORDPRESS_SUBDIR + '/wp-content/plugins/boat-trader-v3/assets/images/' + filename;
  return result;
}

/**
 * Fix image URL to work on any host
 * 
 * Handles these cases:
 * - URL already absolute (http:// or https://): return as-is
 * - URL starts with /wp-content/: prepend home_url (e.g., http://localhost/wordpress/)
 * - URL is just a filename: prepend content_url + /uploads/boat-trader/
 * - URL starts with /uploads/: prepend content_url
 */
export function fixImageUrl(url: string): string {
  if (!url) return '';
  
  // FIX: Handle known static plugin images - use getPluginUrl for these
  // These are images that exist in the plugin's assets folder, not uploaded images
  const staticPluginImages = ['sealevel.png', 'logo.svg', 'logo.png', 'sea-level.png'];
  if (staticPluginImages.includes(url)) {
    // plugin_url now points to assets/images/, so we just append the filename
    return getPluginUrl(url);
  }
  
  // Already a full URL
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // FIX: Handle double boat-trader path issue
  // API returns: /wp-content/uploads/boat-trader/boat-trader/boats/...
  // Should be: /wp-content/uploads/boat-trader/boats/...
  if (url.includes('boat-trader/boat-trader')) {
    url = url.replace(/boat-trader\/boat-trader/, 'boat-trader');
  }
  
  // FIX: Handle dealer logos path - /dealer-logos/logo.jpg should use /wp-content/uploads/dealer-logos/logo.jpg
  if (url.startsWith('/dealer-logos/')) {
    const homeUrl = getHomeUrl();
    const contentUrl = getContentUrl();
    
    // If we have home_url, use it
    if (homeUrl) {
      return homeUrl.replace(/\/$/, '') + '/wp-content/uploads' + url;
    }
    
    // Fallback to content_url + uploads
    if (contentUrl) {
      return contentUrl.replace(/\/$/, '') + '/uploads' + url;
    }
    
    // Final fallback for WordPress subdirectory install
    return '/wordpress/wp-content/uploads' + url;
  }
  
  // If it's an absolute URL, check if it needs /wordpress/ prefix
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // Check if URL is missing /wordpress/ but has /wp-content/
    // This happens when API returns wrong URLs like http://localhost/wp-content/...
    // Instead of hardcoding /wordpress/, we check the home_url
    if (url.includes('/wp-content/')) {
      const homeUrl = getHomeUrl();
      if (homeUrl) {
        // Extract the path from home_url (e.g., /wordpress from http://localhost/wordpress/)
        const urlObj = new URL(homeUrl);
        const pathPrefix = urlObj.pathname.replace(/\/$/, '');
        
        // If home_url has a path (like /wordpress), add it
        if (pathPrefix && !url.includes(pathPrefix + '/wp-content/')) {
          return url.replace(/http(s)?:\/\/([^\/]+)/i, 'http$1://$2' + pathPrefix);
        }
      }
    }
    return url;
  }
  
  // If URL starts with /wp-content/, use home_url
  // URL: /wp-content/uploads/boat-trader/image.jpg
  // Result: http://localhost/wordpress/wp-content/uploads/boat-trader/image.jpg
  if (url.startsWith('/wp-content/')) {
    const homeUrl = getHomeUrl();
    if (homeUrl) {
      // Remove trailing slash from homeUrl if present
      const normalizedHomeUrl = homeUrl.replace(/\/$/, '');
      return normalizedHomeUrl + url;
    }
    // Fallback: return as-is
    return url;
  }
  
  // If URL starts with /uploads/, prepend content_url (without trailing /wp-content/)
  // content_url: http://localhost/wordpress/wp-content/
  // URL: /uploads/boat-trader/image.jpg
  // Result: http://localhost/wordpress/uploads/boat-trader/image.jpg
  if (url.startsWith('/uploads/')) {
    const homeUrl = getHomeUrl();
    if (homeUrl) {
      const normalizedHomeUrl = homeUrl.replace(/\/$/, '');
      return normalizedHomeUrl + url;
    }
    return url;
  }
  
  // Otherwise, assume it's just a filename and prepend the default path
  // URL: image.jpg
  // Result: http://localhost/wordpress/wp-content/uploads/boat-trader/image.jpg
  const homeUrl = getHomeUrl();
  if (homeUrl) {
    const normalizedHomeUrl = homeUrl.replace(/\/$/, '');
    return normalizedHomeUrl + '/wp-content/uploads/boat-trader/' + url;
  }
  
  // Final fallback - assume WordPress is in root (no /wordpress/)
  return '/wp-content/uploads/boat-trader/' + url;
}

/**
 * Fix an array of image URLs
 */
export function fixImageUrls(urls: string[]): string[] {
  return urls.map(url => fixImageUrl(url));
}

/**
 * Fix the main image in an object with images array
 */
export function fixBoatImages(boat: any): any {
  if (!boat) return boat;
  
  if (boat.images && Array.isArray(boat.images)) {
    return {
      ...boat,
      images: fixImageUrls(boat.images)
    };
  }
  
  return boat;
}

/**
 * Fix multiple boats' images
 */
export function fixBoatsImages(boats: any[]): string[] {
  return boats.map(boat => fixBoatImages(boat));
}
